import { apiErrorMessage, apiErrorStatus } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { ConfirmModal } from '@/components/common/Modal';
import { InfoIcon } from '@/components/common/icons';
import { CopyTextButton } from '@/components/review/CopyTextButton';
import { DetectionTypes } from '@/components/review/DetectionTypes';
import { DocumentToolbar, ZOOM_MAX, ZOOM_MIN } from '@/components/review/DocumentToolbar';
import { ExportDialog } from '@/components/review/ExportDialog';
import { FindingsList } from '@/components/review/FindingsList';
import { ManualFindingPopover } from '@/components/review/ManualFindingPopover';
import { PdfPageView } from '@/components/review/PdfPageView';
import { ReviewTopBar } from '@/components/review/ReviewTopBar';
import { SkippedPagesWarning } from '@/components/review/SkippedPagesWarning';
import { useActiveDocument } from '@/hooks/useActiveDocument';
import { usePdfDocument } from '@/hooks/usePdfDocument';
import { useReviewDocument } from '@/hooks/useReviewDocument';
import type { Category, ExportMode, ExportedPdf, FindingModel } from '@/types/documents.types';
import { modeLabelKey } from '@/utils/categories';
import { downloadBlob } from '@/utils/download';
import { findingDisplayCategory, isFindingInExport } from '@/utils/findings';
import { clamp } from '@/utils/geometry';
import { type SelectionCandidate, isNodeInside, resolveSelection } from '@/utils/selection';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

/** Where the popover opens relative to the selection, in pixels. */
const POPOVER_OFFSET = 8;

/** The prototype's three columns; below 860 px they stack and the whole grid scrolls instead. */
const GRID =
  'grid min-h-0 flex-1 grid-cols-[244px_minmax(0,1fr)_332px] max-[1120px]:grid-cols-[212px_minmax(0,1fr)_284px] max-[860px]:auto-rows-max max-[860px]:grid-cols-1 max-[860px]:overflow-auto';

interface PendingSelection {
  text: string;
  candidates: SelectionCandidate[];
  position: { left: number; top: number };
}

/**
 * The review screen, as the prototype lays it out: top bar, mode hint, then "Typy detekce", the
 * page, and "Detekované údaje" side by side.
 *
 * Everything here is a decision about the export — which findings the output acts on — so the
 * tabs, the overlays and both columns read the same finding list and the same "in export" rule.
 */
export const Review: FC = () => {
  const { documentId = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    document: reviewDocument,
    page,
    pageNumber,
    setPageNumber,
    isLoading,
    isExpired,
    error,
    setFindingStatus,
    setCategoryExcluded,
    refresh,
    retry,
  } = useReviewDocument(documentId);
  const { pdf, error: pdfError } = usePdfDocument(documentId);

  const [mode, setMode] = useState<ExportMode>('original');
  const [zoom, setZoom] = useState(1);
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);
  const [savingFindingId, setSavingFindingId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exported, setExported] = useState<(ExportedPdf & { mode: ExportMode }) | null>(null);
  const [pending, setPending] = useState<PendingSelection | null>(null);
  const [manualError, setManualError] = useState<string | null>(null);
  const [isSavingManual, setIsSavingManual] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [, setActiveDocumentId] = useActiveDocument();
  const textLayerRef = useRef<HTMLDivElement>(null);
  const pageAreaRef = useRef<HTMLDivElement>(null);

  // A document that expired in the backend cannot be reviewed; send the user back to upload.
  useEffect(() => {
    if (!isExpired) return;
    navigate('/', { replace: true });
  }, [isExpired, navigate]);

  // Without a finished run there is nothing to review; the upload route owns the progress view.
  // A re-run of the skipped pages is the exception: it is followed from here, so a reload that
  // catches its job in flight (a finding toggled meanwhile) must not bounce the user.
  useEffect(() => {
    if (isLoading || !reviewDocument || retry.isRetrying) return;
    if (reviewDocument.detection?.status !== 'done') navigate('/', { replace: true });
  }, [isLoading, reviewDocument, navigate, retry.isRetrying]);

  // The backend refuses new findings while the re-run goes (422), so a popover still open closes with it.
  useEffect(() => {
    if (retry.isRetrying) setPending(null);
  }, [retry.isRetrying]);

  const categories = reviewDocument?.categories ?? [];
  const excludedCategories = useMemo(
    () => new Set(reviewDocument?.categories.filter((entry) => entry.excluded).map((entry) => entry.key) ?? []),
    [reviewDocument]
  );

  const categoryOf = useCallback(
    (finding: FindingModel) => findingDisplayCategory(finding, categories, excludedCategories),
    [categories, excludedCategories]
  );

  const isInExport = useCallback(
    (finding: FindingModel) => isFindingInExport(finding, excludedCategories),
    [excludedCategories]
  );

  const handleToggleFinding = useCallback(
    async (finding: FindingModel) => {
      setSavingFindingId(finding.id);
      await setFindingStatus(finding.id, finding.status === 'excluded' ? 'active' : 'excluded');
      setSavingFindingId(null);
    },
    [setFindingStatus]
  );

  /**
   * Turn a text selection inside the page into a popover. The selection is mapped to offsets in
   * the backend's page text, because those offsets are what `POST /findings` accepts (ADR 0007).
   */
  const handleTextSelection = useCallback(() => {
    if (!page || retry.isRetrying) return;
    const selection = window.getSelection();
    const selected = selection?.toString() ?? '';
    if (!selection || selection.isCollapsed || selected.trim() === '') {
      setPending(null);
      return;
    }
    if (!isNodeInside(selection.anchorNode, textLayerRef.current)) return;

    const anchorElement =
      selection.anchorNode?.nodeType === Node.TEXT_NODE
        ? selection.anchorNode.parentElement
        : (selection.anchorNode as Element | null);
    const resolution = resolveSelection(page.text, selected, anchorElement?.textContent ?? '');
    if (resolution.kind === 'none') {
      setManualError(t('review.manual.notFound'));
      setPending(null);
      return;
    }

    const rect = selection.getRangeAt(0).getBoundingClientRect();
    const area = pageAreaRef.current?.getBoundingClientRect();
    // The page column scrolls and the popover is positioned in its content, so add the scroll back.
    const scrollTop = pageAreaRef.current?.scrollTop ?? 0;
    const scrollLeft = pageAreaRef.current?.scrollLeft ?? 0;
    setManualError(null);
    setPending({
      text: selected.trim(),
      candidates: resolution.kind === 'unique' ? [resolution.candidate] : resolution.candidates,
      position: {
        left: area ? clamp(rect.left - area.left, 0, Math.max(area.width - 320, 0)) + scrollLeft : 0,
        top: area ? rect.bottom - area.top + scrollTop + POPOVER_OFFSET : 0,
      },
    });
  }, [page, retry.isRetrying, t]);

  const handleCreateFinding = useCallback(
    async (candidate: SelectionCandidate, category: Category) => {
      setIsSavingManual(true);
      setManualError(null);
      try {
        const created = await documentsApi.createFinding(documentId, {
          page: pageNumber,
          start: candidate.start,
          end: candidate.end,
          category,
        });
        setPending(null);
        window.getSelection()?.removeAllRanges();
        await refresh();
        setSelectedFindingId(created.id);
      } catch (err) {
        setManualError(apiErrorMessage(err, t('review.manual.failed')));
      } finally {
        setIsSavingManual(false);
      }
    },
    [documentId, pageNumber, refresh, t]
  );

  /** "Stáhnout: {mode}" produces the export first; the dialog then offers the ready file. */
  const handleExport = useCallback(async () => {
    if (!reviewDocument || reviewDocument.scanned) return;
    setIsExporting(true);
    setExportError(null);
    try {
      const result = await documentsApi.export(documentId, mode, reviewDocument.filename);
      setExported({ ...result, mode });
    } catch (err) {
      setExportError(apiErrorMessage(err, t('review.toolbar.exportFailed')));
    } finally {
      setIsExporting(false);
    }
  }, [documentId, mode, reviewDocument, t]);

  /** The same call as the documents list: `DELETE /documents/{id}`; a 404 is the wanted outcome. */
  const handleDiscard = async () => {
    setIsDiscarding(true);
    try {
      await documentsApi.delete(documentId);
    } catch (err) {
      if (apiErrorStatus(err) !== 404) {
        toast.error(apiErrorMessage(err, t('review.toolbar.discardFailed')));
        setIsDiscarding(false);
        setConfirmDiscard(false);
        return;
      }
    }
    setActiveDocumentId(null);
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center text-[12.5px] text-subtle" role="status">
        {t('common.loading')}
      </div>
    );
  }

  if (!reviewDocument) {
    return (
      <div className="p-5">
        <ErrorMessage message={error ?? t('review.loadFailed')} />
      </div>
    );
  }

  const meta = [
    t('documents.card.pages', { count: reviewDocument.page_count }),
    t('review.meta.categories', { count: categories.length }),
    t('review.meta.inMemory'),
  ].join(' · ');
  const problems = [error, pdfError, exportError, retry.error, pending ? null : manualError].filter(
    (message): message is string => Boolean(message)
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <ReviewTopBar
        filename={reviewDocument.filename}
        meta={meta}
        mode={mode}
        onModeChange={setMode}
        onExport={handleExport}
        isExporting={isExporting || retry.isRetrying}
        isScanned={reviewDocument.scanned}
        copyAction={<CopyTextButton documentId={documentId} mode={mode} disabled={retry.isRetrying} />}
        onDiscard={() => setConfirmDiscard(true)}
      />

      <div className="flex flex-none items-center gap-[9px] border-b border-line bg-white px-5 py-2">
        <InfoIcon size={13} stroke="#2eb6e0" className="flex-none" />
        <div className="text-[11.5px] leading-[1.5] text-muted">{t(`review.modeHint.${mode}`)}</div>
      </div>

      <SkippedPagesWarning
        steps={reviewDocument.detection?.steps ?? []}
        onGoToPage={setPageNumber}
        onRetry={retry.start}
        isRetrying={retry.isRetrying}
      />

      {problems.length > 0 ? (
        <div className="flex flex-none flex-col gap-2 border-b border-line bg-white px-5 py-2">
          {problems.map((message) => (
            <ErrorMessage key={message} message={message} />
          ))}
        </div>
      ) : null}

      <div className={GRID}>
        <DetectionTypes
          categories={categories}
          activeFindings={reviewDocument.active_findings}
          excludedFindings={reviewDocument.excluded_findings}
          disabled={retry.isRetrying}
          onToggle={setCategoryExcluded}
        />

        <div ref={pageAreaRef} className="relative flex min-h-0 flex-col overflow-auto bg-canvas pt-5">
          <PdfPageView
            pdf={pdf}
            page={page}
            pageNumber={pageNumber}
            zoom={zoom}
            mode={mode}
            findings={page?.findings ?? []}
            categoryOf={categoryOf}
            isInExport={isInExport}
            selectedFindingId={selectedFindingId}
            onSelectFinding={setSelectedFindingId}
            textLayerRef={textLayerRef}
            onTextSelection={handleTextSelection}
          />
          <p className="m-0 px-5 pt-3 text-center text-[11.5px] text-faint">{t('review.page.selectionHint')}</p>

          {pending ? (
            <ManualFindingPopover
              candidates={pending.candidates}
              selectedText={pending.text}
              position={pending.position}
              categories={categories}
              isSaving={isSavingManual}
              error={manualError}
              onSubmit={handleCreateFinding}
              onClose={() => {
                setPending(null);
                setManualError(null);
              }}
            />
          ) : null}

          <div className="flex-1" aria-hidden="true" />
          <DocumentToolbar
            pageNumber={pageNumber}
            pageCount={reviewDocument.page_count}
            onPageChange={setPageNumber}
            zoom={zoom}
            onZoomChange={(next) => setZoom(clamp(Number(next.toFixed(2)), ZOOM_MIN, ZOOM_MAX))}
            onZoomFit={() => setZoom(1)}
          />
        </div>

        <FindingsList
          findings={page?.findings ?? []}
          categories={categories}
          excludedCategories={excludedCategories}
          pageNumber={pageNumber}
          mode={mode}
          selectedFindingId={selectedFindingId}
          savingFindingId={savingFindingId}
          disabled={retry.isRetrying}
          onSelect={setSelectedFindingId}
          onToggle={handleToggleFinding}
        />
      </div>

      {confirmDiscard ? (
        <ConfirmModal
          title={t('documents.list.discardTitle')}
          text={t('documents.list.discardMessage')}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          isBusy={isDiscarding}
          onConfirm={handleDiscard}
          onCancel={() => setConfirmDiscard(false)}
        />
      ) : null}

      {exported ? (
        <ExportDialog
          modeName={t(modeLabelKey(exported.mode))}
          filename={exported.filename}
          onDownload={() => {
            downloadBlob(exported.blob, exported.filename);
            setExported(null);
          }}
          onClose={() => setExported(null)}
        />
      ) : null}
    </div>
  );
};
